import React, { useState } from 'react';
import { MapPinIcon, PlusIcon, CalendarIcon, ChevronDownIcon, ArrowLeftIcon, ClockIcon, StarIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { CourtCard } from '../components/CourtCard';
import { MapModal } from '../components/MapModal';
import { getVenueById } from '../data/venues';
import { getCourtsByVenueId } from '../data/courts';
export function VenueDetailScreen() {
  const navigate = useNavigate();
  const {
    venueId
  } = useParams<{
    venueId: string;
  }>();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState('today');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const venue = getVenueById(venueId || '');
  const venueCourts = getCourtsByVenueId(venueId || '');
  if (!venue) {
    return <div className="h-screen w-full flex items-center justify-center bg-[#f8f9fa]" data-id="element-816">
        <div className="text-center" data-id="element-817">
          <h2 className="text-2xl font-bold text-[#1f5070] mb-2" data-id="element-818">
            Venue not found
          </h2>
          <button onClick={() => navigate('/home')} className="text-[#3dc7e3] font-bold" data-id="element-819">
            Return to venues
          </button>
        </div>
      </div>;
  }
  // Date options
  const dateOptions = [{
    id: 'today',
    label: 'Today • Nov 21, 2024',
    day: 'Thursday'
  }, {
    id: 'tomorrow',
    label: 'Tomorrow • Nov 22, 2024',
    day: 'Friday'
  }, {
    id: 'nov23',
    label: 'Saturday • Nov 23, 2024',
    day: 'Saturday'
  }, {
    id: 'nov24',
    label: 'Sunday • Nov 24, 2024',
    day: 'Sunday'
  }, {
    id: 'nov25',
    label: 'Monday • Nov 25, 2024',
    day: 'Monday'
  }];
  const selectedDateLabel = dateOptions.find(d => d.id === selectedDate)?.label || dateOptions[0].label;
  // Convert courts to CourtCard format
  const courtCards = venueCourts.map(court => ({
    id: court.id,
    name: court.name,
    image: court.images[0],
    surface: court.surface,
    type: court.type,
    availability: 'Available',
    availableSlots: Math.floor(Math.random() * 6) + 1,
    price: court.price,
    priceUC: court.priceUC,
    isFavorite: false
  }));
  // Filter courts
  const filteredCourts = courtCards.filter(court => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'indoor') return court.type === 'Indoor';
    if (selectedFilter === 'outdoor') return court.type === 'Outdoor';
    return true;
  });
  const filters = [{
    id: 'all',
    label: 'All Courts'
  }, {
    id: 'indoor',
    label: 'Indoor'
  }, {
    id: 'outdoor',
    label: 'Outdoor'
  }];
  return <div className="h-screen w-full flex flex-col bg-[#f8f9fa]" data-id="element-820">
      {/* Header with Back Button */}
      <div className="bg-white px-4 py-4 flex items-center gap-3 shadow-sm" data-id="element-821">
        <button onClick={() => navigate('/home')} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-822">
          <ArrowLeftIcon className="w-6 h-6 text-[#1f5070]" data-id="element-823" />
        </button>
        <div className="flex-1" data-id="element-824">
          <h1 className="text-lg font-bold text-[#1f5070]" data-id="element-825">{venue.name}</h1>
          <p className="text-xs text-[#666666]" data-id="element-826">{venue.location}</p>
        </div>
        <button onClick={() => navigate('/notifications')} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-827">
          <div className="relative" data-id="element-828">
            <svg className="w-6 h-6 text-[#1f5070]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-829">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" data-id="element-830" />
            </svg>
            <span className="absolute -top-1 -right-1 bg-[#E53935] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-831">
              2
            </span>
          </div>
        </button>
      </div>

      {/* Venue Info Banner */}
      <div className="bg-white px-6 py-5 border-b border-gray-200" data-id="element-832">
        <div className="flex items-start gap-4 mb-4" data-id="element-833">
          <img src={venue.image} alt={venue.name} className="w-20 h-20 rounded-xl object-cover shadow-md" data-id="element-834" />
          <div className="flex-1" data-id="element-835">
            <div className="flex items-center gap-2 mb-2" data-id="element-836">
              <div className="flex items-center gap-1" data-id="element-837">
                <StarIcon className="w-4 h-4 fill-[#FFB300] text-[#FFB300]" data-id="element-838" />
                <span className="text-sm font-bold text-[#1f5070]" data-id="element-839">
                  {venue.rating}
                </span>
              </div>
              <span className="text-xs text-[#666666]" data-id="element-840">
                ({venue.reviews} reviews)
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#666666] mb-1" data-id="element-841">
              <MapPinIcon className="w-4 h-4 text-[#3dc7e3]" data-id="element-842" />
              <span data-id="element-843">{venue.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#666666]" data-id="element-844">
              <ClockIcon className="w-4 h-4 text-[#3dc7e3]" data-id="element-845" />
              <span data-id="element-846">{venue.operatingHours}</span>
            </div>
          </div>
          <button onClick={() => setIsMapOpen(true)} className="p-2.5 bg-[#3dc7e3] hover:bg-[#2db5d1] rounded-full transition-colors shadow-lg active:scale-95" aria-label="Get directions" data-id="element-847">
            <MapPinIcon className="w-5 h-5 text-white" data-id="element-848" />
          </button>
        </div>

        {/* Amenities */}
        <div className="flex flex-wrap gap-2" data-id="element-849">
          {venue.amenities.map((amenity, index) => <span key={index} className="text-xs font-medium text-[#1f5070] bg-[#f8f9fa] px-2.5 py-1 rounded-full" data-id="element-850">
              {amenity}
            </span>)}
        </div>
      </div>

      {/* Date Selector */}
      <div className="bg-white px-6 py-3 border-b border-gray-200 relative" data-id="element-851">
        <button onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)} className="flex items-center gap-2 text-[#1f5070] w-full" data-id="element-852">
          <CalendarIcon className="w-5 h-5 text-[#3dc7e3]" data-id="element-853" />
          <span className="text-base font-bold flex-1 text-left" data-id="element-854">
            {selectedDateLabel}
          </span>
          <ChevronDownIcon className={`w-5 h-5 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} data-id="element-855" />
        </button>

        {isDateDropdownOpen && <>
            <div className="fixed inset-0 z-10" onClick={() => setIsDateDropdownOpen(false)} data-id="element-856" />
            <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-20 max-h-64 overflow-y-auto" data-id="element-857">
              {dateOptions.map(date => <button key={date.id} onClick={() => {
            setSelectedDate(date.id);
            setIsDateDropdownOpen(false);
          }} className={`w-full px-6 py-3 text-left hover:bg-[#f8f9fa] transition-colors ${selectedDate === date.id ? 'bg-[#3dc7e3]/10 border-l-4 border-[#3dc7e3]' : ''}`} data-id="element-858">
                  <div className="flex items-center justify-between" data-id="element-859">
                    <div data-id="element-860">
                      <p className="text-sm font-bold text-[#1f5070]" data-id="element-861">
                        {date.label}
                      </p>
                      <p className="text-xs text-[#666666]" data-id="element-862">{date.day}</p>
                    </div>
                    {selectedDate === date.id && <div className="w-2 h-2 bg-[#3dc7e3] rounded-full" data-id="element-863" />}
                  </div>
                </button>)}
            </div>
          </>}
      </div>

      {/* Filter Pills */}
      <div className="bg-white px-6 py-3 border-b border-gray-200" data-id="element-864">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide" data-id="element-865">
          {filters.map(filter => <button key={filter.id} onClick={() => setSelectedFilter(filter.id)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${selectedFilter === filter.id ? 'bg-[#3dc7e3] text-white' : 'bg-gray-100 text-[#1f5070] hover:bg-gray-200'}`} data-id="element-866">
              {filter.label}
            </button>)}
        </div>
      </div>

      {/* Courts Grid */}
      <div className="flex-1 overflow-auto px-4 py-4" data-id="element-867">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto" data-id="element-868">
          {filteredCourts.map(court => <CourtCard key={court.id} {...court} data-id="element-869" />)}
        </div>

        {filteredCourts.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-870">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4" data-id="element-871">
              <CalendarIcon className="w-8 h-8 text-gray-400" data-id="element-872" />
            </div>
            <h3 className="text-lg font-bold text-[#1f5070] mb-2" data-id="element-873">
              No Courts Available
            </h3>
            <p className="text-sm text-[#666666] max-w-xs" data-id="element-874">
              No courts match your selected filters.
            </p>
          </div>}
      </div>

      {/* Floating Action Button */}
      <button onClick={() => navigate(`/venue/${venueId}/calendar`)} className="fixed bottom-24 right-6 w-14 h-14 bg-[#3dc7e3] rounded-full shadow-lg flex items-center justify-center hover:bg-[#2db5d1] active:scale-95 transition-all z-10" aria-label="Open calendar" data-id="element-875">
        <PlusIcon className="w-6 h-6 text-white" data-id="element-876" />
      </button>

      <BottomNav data-id="element-877" />

      <MapModal isOpen={isMapOpen} onClose={() => setIsMapOpen(false)} venue={venue} data-id="element-878" />
    </div>;
}